import React, { useEffect, useMemo, useRef, useState } from 'react';
import TranslateSelect from '@op/components/SelectComponents/TranslateSelect';
import useLanguage from '@op/hooks/useLanguage';
import SortBySelect from '@op/components/FilterBoxCommon/filters/single-select-factory';
import { languageSelectOptions } from '@op/components/FilterBoxCommon/constant';
import { LanguageType, SentimentType } from '@op/types/common';
import { SortBy } from 'tea-component/lib/table/addons';
import { useTrendingRequest } from '@op/pages/Overview/TrendingPublications/hooks/useTrendingRequest';
import {
  getLogValue,
  useRequestLatestEditLog,
} from '@op/pages/feeds/hooks/useFeedsRequest';
import FigureCard from '@op/pages/Overview/TrendingPublications/components/FigureCard';
import { useUpdateEffect } from 'ahooks';
import CardCommon from '@op/components/CardCommon';
import './index.scss';
import ReviewsModal from '@op/components/ReviewsModal';
import useReviewModalData from '@op/components/ReviewsModal/useReviewModalData';
import GPT, { getPublicationsPromptList } from '@op/components/GPT';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import { getLocale } from 'umi';
import useGeySummary from '@op/components/GPT/hooks/useGetSummary';
import {
  useDrilldownParams,
  EDrilldownMode,
} from '@op/hooks/useDrilldownParams';
import BubbleCommon, {
  channelShareRankingPublicationsTitleTips,
} from '@op/components/BubbleCommon';
import { DEFAULT_MODAL } from '@op/components/GPT/constants';

interface IPublications {
  gameInfo: any;
  filter: any;
  selectedHashtag?: string;
}

const sortByOptions = [
  { value: 'view', text: 'View' },
  // { value: 'share', text: 'Market Share by View' },
  { value: 'follower_number', text: 'Potential Impressions' },
  { value: 'engagement', text: 'Engagement' },
  // { value: 'follower_number', text: 'Publication' },
];

export default function Publications(props: IPublications) {
  const { filter, gameInfo, selectedHashtag = '' } = props;

  const { formatMessage, formatOptionsMessage } = useLanguage();
  const { goPage } = useDrilldownParams();
  const [language, setLanguage] = useState<LanguageType>('origin');
  const [sortBy, setSortBy] = useState<SortBy<any>>({
    by: 'view',
    order: 'desc',
  });

  const [visible, setVisible] = useState(true);
  const currentGameInfo = useRef<any>(null);
  const isEn = getLocale() === 'en-US';

  const hotContentFilter = useMemo(() => {
    const { edition_unified_id, id_type, entity_type } = gameInfo || {};
    return {
      ...filter,
      review_types: ['posts_text', 'posts_video'],
      edition_unified_id,
      id_type,
      entity_type,
      sort_item: sortBy.by,
      sort_model: sortBy.order,
      ...(selectedHashtag
        ? {
            keyword_kind: ['real_hashtag'],
            keyword: [selectedHashtag],
          }
        : {}),
      page_source: 'game_ranking',
    };
  }, [gameInfo, sortBy, filter]);

  const { data, loading } = useTrendingRequest({
    filter: hotContentFilter,
    sortBy,
    type: 'feeds',
    requestMax: false,
  });

  const {
    logCommentMap,
    loading: logLoading,
    requestLogLastEditLog,
  } = useRequestLatestEditLog({
    data: data?.list || [],
    type: ['sentiment'],
    needRequest: true,
  });

  useUpdateEffect(() => {
    requestLogLastEditLog();
  }, []);

  const {
    reviewsVisible,
    data: modalData,
    openModal,
    toggleModal,
  } = useReviewModalData();

  useEffect(() => {
    let timer = null;
    if (
      currentGameInfo.current &&
      currentGameInfo.current?.edition_unified_id !==
        gameInfo?.edition_unified_id
    ) {
      setVisible(false);
      timer = setTimeout(() => {
        setVisible(true);
      }, 100);
    }
    currentGameInfo.current = gameInfo;

    return () => {
      if (timer !== null) clearTimeout(timer);
    };
  }, [gameInfo]);

  const prompt = useMemo(() => {
    if (!gameInfo) {
      return '';
    }
    const promptList = getPublicationsPromptList({ gameInfo });
    return isEn ? promptList?.[0]?.['en-US'] : promptList?.[0]?.['zh-CN'];
  }, [gameInfo, isEn]);

  const { summaryRef, getSummary } = useGeySummary({ isPreviews: false });

  return (
    <CardCommon
      title={
        <BubbleCommon
          tipsMap={channelShareRankingPublicationsTitleTips}
          showIcon={false}
          placement="top"
        >
          <span>
            {selectedHashtag
              ? formatMessage({ id: 'Related Publications' })
              : formatMessage({ id: 'Top 50 Publications' })}
          </span>
        </BubbleCommon>
      }
      contentCard
      hasTitleArrow
      className={'dbo-hot-content-publications-wrapper'}
      handleTitleOnClick={() => {
        goPage({
          path: `/opinion/Overview/TrendingPublications?gameid=${gameInfo?.edition_unified_id}`,
          filter: {
            ...filter,
            channel_type: 'social',
          },
          mode: EDrilldownMode.Local,
        });
        buriedBtnPoint('GotoTop50Post');
      }}
    >
      <>
        {visible && (
          <GPT.PageCard
            needRequestPrompt={false}
            contentCard
            need
            gameInfo={gameInfo}
            saveName="Channel Share Rankings"
            filter={hotContentFilter}
            ref={summaryRef}
            hasStopBtn
            showRefreshBtn={false}
          >
            {(toggleVisible: any, { total }: { total: number }) => (
              <GPT.Button
                onClick={() => {
                  getSummary({
                    modelType: DEFAULT_MODAL,
                    resultType: 'page',
                    prompt,
                    gptOptions: 'basic',
                  });
                }}
                disabled={total === 0}
              ></GPT.Button>
            )}
          </GPT.PageCard>
        )}
      </>

      <CardCommon
        loading={loading}
        loadingHeight={600}
        contentCard
        title={
          <TranslateSelect
            langOptions={formatOptionsMessage(languageSelectOptions)}
            lang={language}
            isSingle={true}
            getChange={(e: any) => {
              setLanguage(e);
              // buriedBtnPoint('TP_Translate', e);
            }}
          />
        }
        dropdownsFilterBeforeElement={
          <SortBySelect
            defaultParams={{
              options: sortByOptions,
              btnTextPrefix: formatMessage({ id: 'Sort By' }),
            }}
            defaultValue={sortBy.by}
            onChange={(_key: any, val: string) => {
              setSortBy({ by: val, order: 'desc' });
              // buriedBtnPoint('TPTP_Sort', { by: val, order: 'desc' });
            }}
          />
        }
        className={'dbo-hot-content-publications'}
        empty={!data || data?.list?.length === 0}
        emptyHeight={600}
      >
        {data?.list?.map((item: any, index: number) => {
          const sentiment = getLogValue(
            logCommentMap,
            item,
            'latest_sentiment',
          ) as SentimentType;

          return (
            <div className="trend-posts-item" key={index}>
              <FigureCard
                size="large"
                item={item}
                direction={'column'}
                loading={logLoading}
                updateSentiment={sentiment || '3'}
                language={language}
                navigatePage={(e: any) => {
                  buriedBtnPoint('ClickPost');
                  openModal(item);
                }}
                buriedBtnPointId={'TPTP_GoToURL'}
              ></FigureCard>
            </div>
          );
        })}
        <ReviewsModal
          visible={reviewsVisible}
          data={modalData}
          language={language}
          close={toggleModal}
          gameInfo={gameInfo}
        />
      </CardCommon>
    </CardCommon>
  );
}
